/* eslint-disable @typescript-eslint/no-explicit-any */
import { autobind } from "core-decorators";
import BaseController from "@/components/mapbox/Lib/Controllers/BaseController";
import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { Point } from "mapbox-gl";
import useMapboxStore from "@/stores/useMapboxStore";
import { getMapboxObject } from "@/components/mapbox/Lib/Utils/ObjectUtils";
import type { MapboxCustomObject } from "../Objects/MapboxCustomObject";

export default class InteractionController extends BaseController {
  public static name = "InteractionController";
  private _hoverFeature: any | null = null;
  private _selectedFeature: any | null = null;
  constructor(mapboxViewer: MapboxViewer) {
    super(mapboxViewer);
  }

  set enabled(value: boolean) {
    this._enabled = value;
    if (value) {
      this._canvas.addEventListener(
        "pointerdown",
        this.onPointerDownController
      );
      this._canvas.addEventListener(
        "pointermove",
        this.onPointerMoveController
      );
      this._canvas.addEventListener("contextmenu", this.handleContextMenuClick);
      this._mapboxViewer._mapViewer.on("zoomstart", this.handleZoomStart);
      window.addEventListener("keydown", this.handleKeyDown);
    } else {
      this.dispose();
    }
  }
  get enabled(): boolean {
    return this._enabled;
  }
  @autobind
  handleZoomStart() {
    // if (useMapboxStore.getState().contextMenu) {
    //   useMapboxStore.getState().setContextMenu(null);
    // }
  }

  @autobind
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.editorDetach();
    }
  }

  @autobind
  protected onPointerDownController(event: PointerEvent) {
    if (!this._enabled) {
      return;
    }

    // Fix: Calculate coordinates relative to canvas, not viewport
    const canvasRect = this._canvas.getBoundingClientRect();
    this._onDownPosition.x = event.clientX - canvasRect.left;
    this._onDownPosition.y = event.clientY - canvasRect.top;
    const canvas = this._mapboxViewer._mapViewer.getCanvas();
    canvas.addEventListener("pointerup", this.onPointerUpController);
    // if (useMapboxStore.getState().contextMenu) {
    //   useMapboxStore.getState().setContextMenu(null);
    // }
  }

  @autobind
  public override onPointerMoveController(event: PointerEvent): void {
    if (!this._enabled) return;
    if (this._selectedFeature) return;

    this._canvas.style.cursor = "auto";

    const canvasRect = this._canvas.getBoundingClientRect();
    this._onMovePosition.x = event.clientX - canvasRect.left;
    this._onMovePosition.y = event.clientY - canvasRect.top;

    // Query for polygon features (including building polygons)
    const features = this._mapboxViewer._mapViewer.queryRenderedFeatures(
      this._onMovePosition,
      {
        filter: ["==", ["get", "app"], "logistic-app"],
      }
    );

    if (features.length) {
      const feature = features[0];
      const object = getMapboxObject(feature, this._mapboxViewer);
      if (!object) {
        return;
      }

      if (this._hoverFeature && this._hoverFeature !== object) {
        if (this._hoverFeature.setUnhighlight) {
          this._hoverFeature.setUnhighlight();
        }
      }

      if (object.setHighlight) {
        object.setHighlight();
        useMapboxStore.getState().setHoverFeature(object as MapboxCustomObject);
        this._canvas.style.cursor = "pointer";
      }
      this._hoverFeature = object;
    } else {
      if (this._hoverFeature && this._hoverFeature.setUnhighlight) {
        this._hoverFeature.setUnhighlight();
      }
      this._hoverFeature = null;
      useMapboxStore.getState().setHoverFeature(null);
    }
  }

  @autobind
  handleContextMenuClick(event: MouseEvent): void {
    event.preventDefault(); // Prevent the default browser context menu
    if (!this._enabled) return;

    // Fix: Calculate coordinates relative to canvas
    const canvasRect = this._canvas.getBoundingClientRect();
    const point = new Point(
      event.clientX - canvasRect.left,
      event.clientY - canvasRect.top
    );

    const features = this._mapboxViewer._mapViewer.queryRenderedFeatures(
      point,
      {
        filter: ["==", ["get", "app"], "logistic-app"],
      }
    );

    if (features.length) {
      if (useMapboxStore.getState().hoverFeature) {
        useMapboxStore.getState().setHoverFeature(null);
      }
      const feature = features[0];
      const object = getMapboxObject(feature, this._mapboxViewer);
      if (object) {
        // useMapboxStore.getState().setContextMenu({
        //   x: event.clientX,
        //   y: event.clientY,
        //   markerIndex: feature.properties?.markerIndex ?? 0,
        //   feature: feature,
        //   object: object,
        // });
      }
    } else {
      //   useMapboxStore.getState().setContextMenu(null);
    }
  }

  @autobind
  public override handleClick(event: PointerEvent): void {
    if (!this._enabled) return;
    if (event.button !== 0) return;

    if (
      this._onDownPosition.x !== this._onUpPosition.x ||
      this._onDownPosition.y !== this._onUpPosition.y
    ) {
      return;
    }

    // Query for polygon features (including building polygons)
    const features = this._mapboxViewer._mapViewer.queryRenderedFeatures(
      this._onDownPosition,
      {
        filter: ["==", ["get", "app"], "logistic-app"],
      }
    );

    if (features.length) {
      const feature = features[0];
      const object = getMapboxObject(feature, this._mapboxViewer);
      if (!object) {
        return;
      }
      if (this._selectedFeature?.contentId === object.contentId) {
        return;
      }

      this.editorDetach();
      this._selectedFeature = object;
      if (object.setUnhighlight) {
        object.setUnhighlight();
      }
      if (object.setSelected) {
        object.setSelected();
      }
      // Enable appropriate editor based on object type
      if (object.content?.type === "building") {
        // Enable building editor
        if (this._mapboxViewer.editors.editorBuildingController) {
          this._mapboxViewer.editors.editorBuildingController.enabled = true;
          this._mapboxViewer.editors.editorBuildingController.attached(
            object as any
          );
        }
      } else if (object.content?.type === "measurement_length") {
        // Enable length measurement editor
        if (this._mapboxViewer.editors.editorMeasureLengthController) {
          this._mapboxViewer.editors.editorMeasureLengthController.enabled =
            true;
          this._mapboxViewer.editors.editorMeasureLengthController.attached(
            object as any
          );
        }
      } else if (object.content?.type === "measurement_area") {
        // Enable area measurement editor
        if (this._mapboxViewer.editors.editorMeasureAreaController) {
          this._mapboxViewer.editors.editorMeasureAreaController.enabled = true;
          this._mapboxViewer.editors.editorMeasureAreaController.attached(
            object as any
          );
        }
      } else if (object.editor) {
        // Enable polygon editor
        object.editor.enabled = true;
        object.editor.attached(object as any);
      }

      this._canvas.style.cursor = "auto";
      useMapboxStore.getState().setHoverFeature(null);
    } else {
      this.editorDetach();
    }
  }

  editorDetach() {
    if (this._selectedFeature) {
      if (this._selectedFeature.editor) {
        this._selectedFeature.editor.enabled = false;
        this._selectedFeature.editor.detached();
      }
      if (this._selectedFeature.setUnhighlight) {
        this._selectedFeature.setUnhighlight();
      }
      this._selectedFeature = null;
    }

    // Disable all measurement editors
    if (this._mapboxViewer.editors.editorMeasureLengthController) {
      this._mapboxViewer.editors.editorMeasureLengthController.enabled = false;
      this._mapboxViewer.editors.editorMeasureLengthController.detached();
    }
    if (this._mapboxViewer.editors.editorMeasureAreaController) {
      this._mapboxViewer.editors.editorMeasureAreaController.enabled = false;
      this._mapboxViewer.editors.editorMeasureAreaController.detached();
    }
  }

  @autobind
  dispose() {
    super.dispose();
    this.editorDetach();
    this._canvas.removeEventListener(
      "pointerdown",
      this.onPointerDownController
    );
    this._canvas.removeEventListener(
      "pointermove",
      this.onPointerMoveController
    );
    this._canvas.removeEventListener(
      "contextmenu",
      this.handleContextMenuClick
    );
    window.removeEventListener("keydown", this.handleKeyDown);
    this._mapboxViewer._mapViewer.off("zoomstart", this.handleZoomStart);
  }
}
